import React, { useCallback } from 'react'
import IconButton from 'LuxKit/Button/IconButton'
import LinePlayIcon from 'LuxKit/Icons/line/LinePlayIcon'
import LinePauseIcon from 'LuxKit/Icons/line/LinePauseIcon'
import LineVolumeUpIcon from 'LuxKit/Icons/line/LineVolumeUpIcon'
import LineVolumeMuteIcon from 'LuxKit/Icons/line/LineVolumeMuteIcon'
import LineShareIcon from 'LuxKit/Icons/line/LineShareIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { isHeadlessModeSel } from 'selectors/configSelectors'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions } from 'contexts/Wrapped'
import useWrappedShare from '../../hooks/useWrappedShare'

interface Props {
  onClose: () => void
}

function WrappedHeaderActions({ onClose }: Props) {
  const dispatch = useWrappedStoryDispatch()
  const { handleShare } = useWrappedShare()
  const isHeadless = useAppSelector(isHeadlessModeSel)

  // Use granular selectors to minimize re-renders
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isMuted = useWrappedStorySelector(state => state.isMuted)

  const handleTogglePause = useCallback(() => {
    dispatch({ type: WrappedStoryActions.togglePause, isPaused: undefined })
  }, [dispatch])

  const handleToggleMute = useCallback(() => {
    dispatch({ type: WrappedStoryActions.toggleMute, isMuted: undefined })
  }, [dispatch])

  return (
    <Group direction="horizontal" gap={4} verticalAlign="center">
      <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleTogglePause}
        aria-label={isPaused ? 'Play' : 'Pause'}
      >
        {isPaused ? <LinePlayIcon /> : <LinePauseIcon />}
      </IconButton>
      <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleToggleMute}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? <LineVolumeMuteIcon /> : <LineVolumeUpIcon />}
      </IconButton>
      <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleShare}
        aria-label="Share"
      >
        <LineShareIcon />
      </IconButton>
      {!isHeadless && (
        <IconButton
          kind="tertiary"
          variant="ghost"
          size="small"
          onClick={onClose}
          aria-label="Close"
        >
          <LineTimesIcon />
        </IconButton>
      )}
    </Group>
  )
}

export default React.memo(WrappedHeaderActions)
